import { IconButton } from '@mui/material';
import {
  getGridDateOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
} from '@mui/x-data-grid-pro';

import { IcDotSpread } from '@/assets/images';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/TailwindComponents';

export interface InternalAffairsActions {
  onView: (row: any) => void;
  onEdit: (row: any) => void;
  onExport: (row: any) => void;
  onEmail: (row: any) => void;
  onDelete: (row: any) => void;
}

const statusOptions = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'CLOSED', label: 'Closed' },
];

const caseTypeOptions = [
  { value: 'USE_OF_FORCE', label: 'Use of Force' },
  { value: 'OFFICER_MISCONDUCT', label: 'Officer Misconduct' },
  { value: 'CITIZEN_COMPLAINT', label: 'Citizen Complaint' },
  { value: 'INTERNAL_COMPLAINT', label: 'Internal Complaint' },
  { value: 'POLICY_VIOLATION', label: 'Policy Violation' },
];

const statusDisplayMap: Record<string, string> = Object.fromEntries(
  statusOptions.map(({ value, label }) => [value, label]),
);

const statusColorMap: Record<string, string> = {
  OPEN: '#2e7d32',
  IN_PROGRESS: '#ed6c02',
  CLOSED: '#757575',
};

const caseTypeDisplayMap: Record<string, string> = Object.fromEntries(
  caseTypeOptions.map(({ value, label }) => [value, label]),
);

export const internalAffairsCols = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: any) => void,
  actions?: InternalAffairsActions,
): Record<string, () => Partial<GridColDef>> => ({
  caseId: () => ({
    field: 'caseId',
    headerName: 'Case ID',
    minWidth: 180,
    filterOperators: getGridStringOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
  }),
  officersWithAccess: () => ({
    field: 'officersWithAccess',
    headerName: 'Officer(s) with access',
    minWidth: 260,
    flex: 1,
    filterOperators: getGridStringOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
  }),
  caseType: () => ({
    field: 'caseType',
    headerName: 'Case Type',
    minWidth: 180,
    type: 'singleSelect',
    valueOptions: caseTypeOptions,
    filterOperators: getGridSingleSelectOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
    valueFormatter: (value: string) => caseTypeDisplayMap[value] || value,
  }),
  status: () => ({
    field: 'status',
    headerName: 'Status',
    minWidth: 160,
    type: 'singleSelect',
    valueOptions: statusOptions,
    filterOperators: getGridSingleSelectOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
    valueFormatter: (value: string) => statusDisplayMap[value] || value,
    renderCell: (params) => {
      const displayLabel = statusDisplayMap[params.value] || params.value;
      return (
        <span
          style={{
            color: statusColorMap[params.value] || '#000',
            fontWeight: 500,
          }}
        >
          {displayLabel}
        </span>
      );
    },
  }),
  createdBy: () => ({
    field: 'createdBy',
    headerName: 'Created By',
    minWidth: 180,
    filterOperators: getGridStringOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
  }),
  createdAt: () => ({
    field: 'createdAt',
    headerName: 'Created At',
    minWidth: 180,
    type: 'dateTime',
    filterOperators: getGridDateOperators(true),
    valueGetter: (value: string) => (value ? new Date(value) : null),
    valueFormatter: (value: Date | string | null) => {
      if (!value) return '';
      const date = value instanceof Date ? value : new Date(value);
      if (isNaN(date.getTime())) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${month}/${day}/${year} ${hours}:${minutes}`;
    },
  }),
  actions: () => ({
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    width: 120,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params) =>
      actions ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton onClick={(e) => e.stopPropagation()}>
              <IcDotSpread />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center">
            <DropdownMenuItem
              onClick={() => actions.onView(params.row)}
              className="cursor-pointer font-semibold"
            >
              View
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => actions.onEdit(params.row)}
              className="cursor-pointer font-semibold"
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => actions.onExport(params.row)}
              className="cursor-pointer font-semibold"
            >
              Export
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => actions.onEmail(params.row)}
              className="cursor-pointer font-semibold"
            >
              Email
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => actions.onDelete(params.row)}
              className="cursor-pointer font-semibold text-red-600"
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            if (openMenu) {
              openMenu(e, params.row);
            }
          }}
        >
          <IcDotSpread />
        </IconButton>
      ),
  }),
});
